/**
 * Helpers puros da sub-aba "Nomes e Dimensões" (Produtos).
 *
 * Livres de React e de serviços para permitir testes unitários (vitest).
 */
import type {
  DimensionField,
  ProductDimensionItem,
  ProductDimensionsPayload,
} from '@/types/product';

/** Rascunho de edição de uma linha: os quatro campos como texto do input. */
export interface DimensionDraft {
  width: string;
  height: string;
  length: string;
  total_weight: string;
}

/** Quais campos divergem dentro de um grupo de nome. */
export type DimensionDivergence = Record<DimensionField, boolean> & { any: boolean };

export const DIMENSION_FIELDS: DimensionField[] = ['width', 'height', 'length', 'total_weight'];

/**
 * Marca os campos cujas medidas NÃO são iguais em todos os produtos do grupo.
 *
 * `null`/`undefined` conta como um valor próprio: um produto sem medida ao lado
 * de um produto com medida É divergência — e é justamente o caso mais comum.
 * (Por isso a regra não pode virar `COUNT(DISTINCT ...)` no SQL, que ignora
 * NULL e devolveria "sem divergência" nesse cenário.)
 *
 * Grupo com um único produto nunca diverge.
 */
export const computeDimensionDivergence = (items: ProductDimensionItem[]): DimensionDivergence => {
  const result = {
    width: false,
    height: false,
    length: false,
    total_weight: false,
    any: false,
  } as DimensionDivergence;

  if (items.length < 2) return result;

  for (const field of DIMENSION_FIELDS) {
    const first = items[0][field] ?? null;
    const diverges = items.some((item) => (item[field] ?? null) !== first);
    result[field] = diverges;
    if (diverges) result.any = true;
  }

  return result;
};

/** Valor do input a partir do dado do produto (vazio quando não cadastrado). */
export const toDraftValue = (value: number | null | undefined): string =>
  value === null || value === undefined ? '' : String(value);

export const buildDraft = (item: ProductDimensionItem): DimensionDraft => ({
  width: toDraftValue(item.width),
  height: toDraftValue(item.height),
  length: toDraftValue(item.length),
  total_weight: toDraftValue(item.total_weight),
});

/**
 * Lê largura/altura/comprimento do input.
 *
 * Devolve `null` para campo vazio e `undefined` para entrada inválida — a
 * distinção importa: as colunas são INTEIRAS no banco, e mandar `60.5` faria o
 * binding do Go falhar com um 400 críptico. Melhor barrar antes.
 */
export const parseDimensionInt = (raw: string): number | null | undefined => {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  if (!/^\d+$/.test(trimmed)) return undefined;

  const value = Number(trimmed);
  return Number.isSafeInteger(value) && value >= 0 && value <= 100000 ? value : undefined;
};

/**
 * Lê o peso do input, aceitando vírgula decimal (pt-BR).
 *
 * Limitado a 4 casas porque `TotalWeight` é `decimal(18,4)` — mandar mais casas
 * seria truncado em silêncio pelo banco.
 */
export const parseWeight = (raw: string): number | null | undefined => {
  const trimmed = raw.trim().replace(',', '.');
  if (trimmed === '') return null;
  if (!/^\d+(\.\d{1,4})?$/.test(trimmed)) return undefined;

  const value = Number(trimmed);
  return Number.isFinite(value) && value >= 0 && value <= 10000 ? value : undefined;
};

/**
 * Monta o corpo do PATCH com apenas o que MUDOU.
 *
 * Devolve `null` quando nada mudou (evita uma gravação inútil que só carimbaria
 * `LastUpdatedBy`) e `undefined` quando algum campo está inválido.
 *
 * Limpar uma medida (deixar o campo vazio) não é suportado: campo ausente
 * significa "não alterar" no endpoint, e um ponteiro nulo em Go não distingue
 * ausente de null. Apagar o valor no input simplesmente mantém o que estava.
 */
export const buildDimensionsPayload = (
  draft: DimensionDraft,
  original: ProductDimensionItem
): ProductDimensionsPayload | null | undefined => {
  const width = parseDimensionInt(draft.width);
  const height = parseDimensionInt(draft.height);
  const length = parseDimensionInt(draft.length);
  const weight = parseWeight(draft.total_weight);

  if (width === undefined || height === undefined || length === undefined || weight === undefined) {
    return undefined;
  }

  const payload: ProductDimensionsPayload = {};
  if (width !== null && width !== (original.width ?? null)) payload.width = width;
  if (height !== null && height !== (original.height ?? null)) payload.height = height;
  if (length !== null && length !== (original.length ?? null)) payload.length = length;
  if (weight !== null && weight !== (original.total_weight ?? null)) payload.total_weight = weight;

  return Object.keys(payload).length > 0 ? payload : null;
};

/** Exibe uma medida inteira; travessão quando não cadastrada. */
export const formatDimension = (value: number | null | undefined): string =>
  value === null || value === undefined ? '—' : value.toLocaleString('pt-BR');

/** Exibe o peso com até 3 casas; travessão quando não cadastrado. */
export const formatWeight = (value: number | null | undefined): string =>
  value === null || value === undefined
    ? '—'
    : value.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
